import { FC, useEffect, useRef, useState } from "react";
import { CommonListParams } from "@/types/common";
import { FactoryInventoryHistory } from "@/types/factoryInventory";
import { fInventoryChanges } from "@/apis/factoryInventory";
import { Flex, Modal, TableProps } from "antd";
import { useTranslation } from "react-i18next";
import useDataList from "@/hooks/useDataList";
import STableLayout, { DEFAULT_PAGE_SIZE, TableLayoutRef } from "@/styles/STableLayout";
import { AnyObject } from "antd/es/_util/type";
import SText from "@/styles/SText";
import useFactoryEnums from "../hooks/useFactoryEnums";
import { numFormmater } from "@/pages/Requirement/util";
import WMSAvatar from "@/styles/WMSAvatar";
import { getUserAvatarTip, getUserName } from "@/utils/user";

interface ChangeListProps {
  productId: string;
  onClose: () => void;
}

const ChangeList: FC<ChangeListProps> = ({ productId, onClose }) => {
  const { t } = useTranslation();
  const { getSourceType, getOpType, getDirection } = useFactoryEnums();
  const tableLayoutRef = useRef<TableLayoutRef>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const [list, total, loading, { fetchList }] = useDataList<FactoryInventoryHistory, [string, CommonListParams]>({
    fetchApi: fInventoryChanges
  });

  useEffect(() => {
    fetch(0);
    return () => abortControllerRef.current?.abort();
  }, [productId]);

  const fetch = (offset: number) => {
    if (offset === 0) tableLayoutRef.current?.resetTablePage();
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    fetchList(
      productId,
      {
        pagination: {
          offset,
          take: tableLayoutRef.current?.pageSize ?? DEFAULT_PAGE_SIZE,
        },
        controller
      });
  };

  const columns: TableProps<FactoryInventoryHistory>['columns'] = [
    {
      title: t('warehouse.batchNo'),
      key: 'lot',
      width: 220,
      fixed: 'left',
      render: (_, record) => <SText text={record.batchNo || ''} />
    },
    {
      title: <SText text={t("product.code")} />,
      key: "productCode",
      width: 120,
      render: (_, record) => (
        <SText
          text={record.product?.code || ""}
          style={{ width: "100%" }}
        />
      )
    },
    {
      title: <SText text={t("product.name")} />,
      key: "productName",
      width: 150,
      render: (_, record) => (
        <SText
          text={record.product?.name || ""}
          style={{ width: "100%" }}
        />
      )
    },
    {
      title: <SText text={t("sale.prodMod")} />,
      key: "productModel",
      width: 160,
      render: (_, record) => (
        <SText
          text={record.product?.model || ""}
          style={{ width: "100%" }}
        />
      )
    },
    {
      title: <SText text={t("product.uom")} />,
      key: "uom",
      width: 80,
      align: "center",
      render: (_, record) => (
        <SText text={record.product?.uom || ""} style={{ width: "100%" }} />
      )
    },
    {
      title: <SText text={t("product.materialProcess")} />,
      key: "materialProcess",
      width: 160,
      render: (_, record) => (
        <SText text={record.product?.process || ""} style={{ width: "100%" }} />
      )
    },
    {
      title: t('warehouse.sourceType'),
      key: 'sourceType',
      width: 100,
      render: (_, record) => {
        let info = getSourceType(record.sourceType);
        return info && <SText tagMode text={info.label} color={info.color} />
      }
    },
    {
      title: t('inventory.opType'),
      key: 'opType',
      width: 100,
      render: (_, record) => {
        let info = getOpType(record.opType);
        return info && <SText tagMode text={info.label} color={info.color} />
      }
    },
    {
      title: t('inventory.inventoryType'),
      key: 'direction',
      width: 100,
      render: (_, record) => {
        let info = getDirection(record.direction);
        return info && <SText tagMode text={info.label} color={info.color} />
      }
    },
    {
      title: t('inventory.beforeCount'),
      key: 'beforeCount',
      width: 120,
      render: (_, record) => numFormmater(record.beforeCount)
    },
    {
      title: t('inventory.changeCount'),
      key: 'changeCount',
      width: 120,
      render: (_, record) => numFormmater(record.changeCount)
    },
    {
      title: t('inventory.afterCount'),
      key: 'afterCount',
      width: 120,
      render: (_, record) => numFormmater(record.afterCount)
    },
    {
      title: t('counting.operator'),
      dataIndex: "creator",
      key: "creator",
      width: 190,
      render: (creator) => (
        creator ? <>
          <WMSAvatar
            avatarId={creator.avatar}
            uniChar={creator.id}
            creator={creator}
            alt={getUserAvatarTip(creator)}
            style={{ width: '30px', height: '30px', marginRight: '6px' }}
          />
          <span>{getUserName(creator)}</span>
        </>
          : <>-</>
      ),
    },

  ];

  return (
    <Modal
      title={t("inventory.list")}
      open={!!productId}
      width={1200}
      onCancel={onClose}
      footer={null}
      maskClosable={false}
    >
      <Flex vertical style={{ height: "70vh" }}>
        <STableLayout
          columns={columns as Array<AnyObject>}
          tableLayoutRef={tableLayoutRef}
          dataSource={list}
          loading={loading}
          headerChildren={null}
          total={total}
          handleChange={fetch}
          rowKey={"id"}
          scrollX={1000}
        />
      </Flex>
    </Modal>
  );
};

export default ChangeList;
